import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDaysIcon, ChevronRightIcon, TagIcon } from '@heroicons/react/24/outline';

import PostsCard from '../../shared/components/PostsCard';
import FadeInImage from '../../shared/components/FadeInImage';
import useVideoEmbeds from '../../shared/hooks/useVideoEmbeds';
import * as API from '../../api';

/**
 * Premium /post/:id (blog detail) page — rebuilt on the same Tailwind design
 * system as the redesigned Events Detail page (screens/Events/Detail/index.js):
 * breadcrumb, hero image, font-display heading, and a `prose` container for
 * the dangerouslySetInnerHTML description.
 *
 * Embedded video iframes are post-processed by the same shared hook Events
 * Detail uses (shared/hooks/useVideoEmbeds.js): raw Vimeo/YouTube <iframe>
 * embeds are swapped for a click-to-play thumbnail + play button, since the
 * embed's own chrome doesn't reflow well at small widths. The `[&_iframe]:`
 * classes on the prose container stay as a CSS fallback for any embed the
 * hook doesn't recognize, matching Events Detail exactly.
 *
 * Data flow unchanged: API.Post(id) -> { data } single post object.
 */

const MONTH_NAMES_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTH_NAMES_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function DetailSkeleton() {
  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="h-4 w-40 animate-pulse rounded bg-slate-100" />
      <div className="mt-4 h-8 w-full animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-8 w-2/3 animate-pulse rounded bg-slate-100" />
      <div className="mt-6 aspect-[16/9] w-full animate-pulse rounded-2xl bg-slate-100" />
      <div className="mt-6 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-3.5 w-full animate-pulse rounded bg-slate-100" />
        ))}
      </div>
    </div>
  );
}

function NotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-container flex-col items-center justify-center px-4 py-20 text-center">
      <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-brand-50">
        <TagIcon className="h-11 w-11 text-brand-300" />
        <span className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-base font-bold text-brand-900">
          ?
        </span>
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">
        {t('blog.notFoundTitle')}
      </h1>
      <p className="mt-2.5 max-w-md text-sm text-ink-muted sm:text-base">
        {t('blog.notFoundBody')}
      </p>
      <Link
        to="/blog"
        className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
      >
        {t('blog.notFoundCta')}
        <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" />
      </Link>
    </div>
  );
}

function Details() {
  const { t } = useTranslation();
  const [post, setPost] = React.useState({});
  const [loadingPost, setLoadingPost] = React.useState(false);
  const { id } = useParams();
  const descriptionRef = React.useRef(null);

  // Post-process the CMS-authored description HTML after it mounts: replace
  // any raw video <iframe> embeds with a click-to-play placeholder (see
  // shared/hooks/useVideoEmbeds.js for why). Must run before the early
  // returns below to satisfy the Rules of Hooks.
  //
  // Deps include `loadingPost`: API.Post()'s setPost() and setLoadingPost()
  // are two separate un-batched updates (they fire from a promise chain
  // outside a React event handler), so they land in two separate renders —
  // the description div (and its ref) only mounts once loadingPost flips to
  // false, one render after `post` itself changes. Keying on post?.id alone
  // would miss that second render and leave the ref unprocessed.
  useVideoEmbeds(descriptionRef, [loadingPost, post?.id, post?.description]);

  React.useEffect(() => {
    setLoadingPost(true);
    API.Post(id)
      .then((res) => {
        setPost(res.data);
      })
      .catch(() => {})
      .then(() => {
        setLoadingPost(false);
      });
  }, [0, id]);

  React.useEffect(() => {
    if (post?.title) {
      document.title = `${API.APP_NAME} - ${post.title}`;
    }
  }, [post]);

  const category = post.categories && post.categories[0];

  return (
    <div className="bg-surface">
      {loadingPost ? (
        <DetailSkeleton />
      ) : Object.keys(post).length ? (
        <>
          <div className="mx-auto w-full max-w-4xl px-4 pt-6 sm:px-6 sm:pt-8 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-ink-subtle" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-500">
                {t('detail.breadcrumbHome')}
              </Link>
              <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
              <Link to="/blog" className="hover:text-brand-500">
                {t('nav.blog', 'Blog')}
              </Link>
              <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
              <span className="truncate text-ink-muted">{post.title}</span>
            </nav>

            {/* Title block */}
            <div className="mt-4">
              {category ? (
                <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
                  {category.name}
                </span>
              ) : null}
              <h1 className="mt-2.5 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-[34px]">
                {post.title}
              </h1>
              <div className="mt-2.5 flex items-center gap-1.5 text-sm text-ink-muted">
                <CalendarDaysIcon className="h-4 w-4 text-brand-500" />
                {formatDate(post.created_at)}
              </div>
            </div>

            {/* Hero image */}
            {post.image ? (
              <div className="mt-6 overflow-hidden rounded-2xl shadow-card">
                <FadeInImage
                  src={post.image}
                  alt={post.title}
                  eager
                  className="aspect-[16/9] w-full object-cover"
                />
              </div>
            ) : null}

            {/* Description — embedded video iframes are swapped for a
                click-to-play placeholder by useVideoEmbeds (see import),
                matching the Events Detail page. The [&_iframe]: rules are a
                CSS fallback for any embed the hook doesn't recognize. */}
            <div
              ref={descriptionRef}
              className="prose prose-sm max-w-none py-8 text-ink-muted [&_a]:font-medium [&_a]:text-brand-500 [&_a:hover]:underline [&_h2]:text-ink [&_h3]:text-ink [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:max-w-xl [&_iframe]:rounded-xl [&_img]:rounded-xl [&_p]:my-2 [&_strong]:text-ink"
              dangerouslySetInnerHTML={{ __html: post.description }}
            />
          </div>

          <div className="mt-4">
            <PostsCard titleSection={t('blog.relatedTitle')} />
          </div>
        </>
      ) : (
        <NotFoundState />
      )}
    </div>
  );
}

export default Details;
